import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Space, Table, Tag, Tooltip, Typography } from "antd";
import {
  ApiOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useConfirm } from "../../../hooks/useConfirm";
import FtpConnectionModal from "./FtpConnectionModal";
import { FTP_ENDPOINT, FTP_PROTOCOL, FTP_PROTOCOL_LABEL } from "../constants";

const { Text } = Typography;

/**
 * The FTP targets a backup archive can be shipped to. Passwords are stored encrypted and are
 * never returned by the API, so this screen only ever knows whether one is on file.
 */
const FtpConnectionsPanel = ({ onChanged }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effect — see the note in BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const { confirmAction } = useConfirm();

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    await mutateRef.current("listFtpConnections", {
      method: "get",
      endpoint: FTP_ENDPOINT,
      onSuccess: (data) => setConnections(data || []),
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saved = () => {
    load();
    onChanged?.();
  };

  const test = async (record) => {
    setTesting(record.ftpConnectionId);

    await mutate("testFtpConnection", {
      method: "post",
      endpoint: `${FTP_ENDPOINT}/test`,
      // The stored password stays on the server: sending the id lets it fill the blank in.
      payload: { ...record, password: "", ftpConnectionId: record.ftpConnectionId },
      onSuccess: load,
    });

    setTesting(null);
  };

  const columns = [
    {
      title: "Connection",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Space size={8}>
            <Text strong>{name}</Text>
            {!record.isActive && <Tag>inactive</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {record.username}@{record.host}:{record.port}
          </Text>
        </Space>
      ),
    },
    {
      title: "Protocol",
      dataIndex: "protocol",
      key: "protocol",
      width: 140,
      render: (protocol) => (
        <Tag color={protocol === FTP_PROTOCOL.FTP ? "default" : "green"}>
          {FTP_PROTOCOL_LABEL[protocol]}
        </Tag>
      ),
    },
    {
      title: "Directory",
      dataIndex: "remoteDirectory",
      key: "remoteDirectory",
      width: 260,
      render: (directory) => <Text code>{directory || "login directory"}</Text>,
    },
    {
      title: "Last test",
      key: "lastTest",
      width: 190,
      render: (_, record) => {
        if (!record.lastTestAt) return <Text type="secondary">Never tested</Text>;

        return (
          <Space direction="vertical" size={0}>
            <Tag color={record.lastTestOk ? "success" : "error"}>
              {record.lastTestOk ? "Reachable" : "Failed"}
            </Tag>
            <Tooltip title={record.lastTestError || ""}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {new Date(record.lastTestAt).toLocaleString()}
              </Text>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="Test connection">
            <Button
              variant="ghost"
              icon={<ApiOutlined />}
              loading={testing === record.ftpConnectionId}
              onClick={() => test(record)}
            />
          </Tooltip>

          <Tooltip title="Edit">
            <Button
              variant="ghost"
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(record);
                setModalOpen(true);
              }}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Button
              variant="ghost"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                confirmAction({
                  key: "deleteFtpConnection",
                  method: "delete",
                  endpoint: `${FTP_ENDPOINT}/${record.ftpConnectionId}`,
                  title: `Delete ${record.name}?`,
                  content:
                    "Archives already uploaded stay on the remote server. If the automatic backup uses this target, its upload is switched off.",
                  okText: "Delete",
                  okType: "danger",
                  onSuccess: saved,
                })
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Where archives are copied to"
        description="Passwords are stored encrypted and never leave the server. Turn the upload on for automatic backups in the schedule above, or tick 'Send to FTP' when you take one by hand."
      />

      <Card
        title={
          <Space size={8}>
            <CloudUploadOutlined />
            FTP connections
          </Space>
        }
        extra={
          <Space>
            <Button variant="secondary" icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              New connection
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="ftpConnectionId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={connections}
          pagination={false}
          scroll={{ x: 1020 }}
        />
      </Card>

      <FtpConnectionModal
        open={modalOpen}
        connection={editing}
        onClose={() => setModalOpen(false)}
        onSaved={saved}
      />
    </Space>
  );
};

export default FtpConnectionsPanel;
